import { Alert, DatePicker, Form, Input, InputNumber, Select, Switch, TimePicker } from "antd";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormModal } from "@/shared/components/FormModal";
import {
	PATTERN_KINDS,
	type Schedule,
	type ScheduleFormValues,
	type ScheduleInput,
	SHIFT_TYPES,
	timeToDayjs,
	WEEKDAY_VALUES,
} from "../types";

const MODAL_WIDTH = 640;
const TIME_FORMAT = "HH:mm:ss";
const MIN_CYCLE_DAYS = 2;
const MAX_CYCLE_DAYS = 31;

interface ScheduleFormModalProps {
	open: boolean;
	editing: Schedule | null;
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (input: ScheduleInput) => void;
}

const NEW_SCHEDULE_VALUES = {
	shiftType: "kunduzgi",
	patternKind: "weekly",
	crossesMidnight: false,
} as const;

/**
 * Forma qiymatlari → so'rov tanasi.
 *
 * Faol bo'lmagan tarmoqning maydonlari ATAYLAB `null` yuboriladi: naqsh
 * `weekly` dan `cyclic` ga o'zgartirilganda eski `weekdays` bazada qolib
 * ketsa, generator qaysi tarmoqni o'qishi noaniq bo'lardi.
 */
function toScheduleInput(values: ScheduleFormValues): ScheduleInput {
	const isWeekly = values.patternKind === "weekly";

	return {
		name: values.name,
		shiftType: values.shiftType,
		patternKind: values.patternKind,
		weekdays: isWeekly ? (values.weekdays ?? []) : null,
		cycleDays: isWeekly ? null : (values.cycleDays ?? null),
		workDays: isWeekly ? null : (values.workDays ?? null),
		anchorDate: isWeekly ? null : (values.anchorDate?.format("YYYY-MM-DD") ?? null),
		startTime: values.startTime.format(TIME_FORMAT),
		endTime: values.endTime.format(TIME_FORMAT),
		lunchStart: values.lunchStart ? values.lunchStart.format(TIME_FORMAT) : null,
		lunchEnd: values.lunchEnd ? values.lunchEnd.format(TIME_FORMAT) : null,
		crossesMidnight: values.crossesMidnight ?? false,
	};
}

function toInitialValues(schedule: Schedule): ScheduleFormValues {
	return {
		name: schedule.name,
		shiftType: schedule.shiftType,
		patternKind: schedule.patternKind,
		weekdays: schedule.weekdays ?? undefined,
		cycleDays: schedule.cycleDays ?? undefined,
		workDays: schedule.workDays ?? undefined,
		anchorDate: schedule.anchorDate ? dayjs(schedule.anchorDate) : undefined,
		startTime: timeToDayjs(schedule.startTime),
		endTime: timeToDayjs(schedule.endTime),
		lunchStart: schedule.lunchStart ? timeToDayjs(schedule.lunchStart) : undefined,
		lunchEnd: schedule.lunchEnd ? timeToDayjs(schedule.lunchEnd) : undefined,
		crossesMidnight: schedule.crossesMidnight,
	};
}

function WeeklyFields() {
	const { t } = useTranslation("schedules");

	const weekdayOptions = WEEKDAY_VALUES.map((day) => ({
		value: day,
		label: t(`weekday.${day}`),
	}));

	return (
		<Form.Item
			name="weekdays"
			label={t("scheduleForm.weekdays.label")}
			rules={[{ required: true, message: t("scheduleForm.weekdays.required") }]}
			extra={t("scheduleForm.weekdays.extra")}
		>
			<Select
				mode="multiple"
				options={weekdayOptions}
				placeholder={t("scheduleForm.weekdays.placeholder")}
			/>
		</Form.Item>
	);
}

/**
 * Backend ham rad etadi (`messages.schedules.workDaysExceedCycle`), lekin bu
 * xatoni formani yubormasdan ushlash arzonroq.
 */
function validateWorkDays(
	value: number | undefined,
	cycleDays: number | undefined,
	t: TFunction
): Promise<void> {
	if (value !== undefined && cycleDays !== undefined && value > cycleDays) {
		return Promise.reject(new Error(t("scheduleForm.workDaysExceedsCycle")));
	}

	return Promise.resolve();
}

function CyclicFields() {
	const { t } = useTranslation("schedules");

	return (
		<>
			<Form.Item
				name="cycleDays"
				label={t("scheduleForm.cycleDays.label")}
				rules={[{ required: true, message: t("scheduleForm.cycleDays.required") }]}
				extra={t("scheduleForm.cycleDays.extra")}
			>
				<InputNumber min={MIN_CYCLE_DAYS} max={MAX_CYCLE_DAYS} className="w-full" />
			</Form.Item>

			<Form.Item
				name="workDays"
				label={t("scheduleForm.workDays.label")}
				dependencies={["cycleDays"]}
				rules={[
					{ required: true, message: t("scheduleForm.workDays.required") },
					({ getFieldValue }) => ({
						validator: (_, value: number | undefined) =>
							validateWorkDays(value, getFieldValue("cycleDays") as number | undefined, t),
					}),
				]}
			>
				<InputNumber min={1} max={MAX_CYCLE_DAYS} className="w-full" />
			</Form.Item>

			<Form.Item
				name="anchorDate"
				label={t("scheduleForm.anchorDate.label")}
				rules={[{ required: true, message: t("scheduleForm.anchorDate.required") }]}
				extra={t("scheduleForm.anchorDate.extra")}
			>
				<DatePicker className="w-full" />
			</Form.Item>
		</>
	);
}

export function ScheduleFormModal({
	open,
	editing,
	isSubmitting,
	onCancel,
	onSubmit,
}: ScheduleFormModalProps) {
	const { t } = useTranslation("schedules");
	const isEditing = editing !== null;

	const shiftTypeOptions = SHIFT_TYPES.map((type) => ({
		value: type,
		label: t(`shiftType.${type}`),
	}));

	const patternKindOptions = PATTERN_KINDS.map((kind) => ({
		value: kind,
		label: t(`patternKind.${kind}`),
	}));

	// `FormModal` `initialValues` HAVOLASI o'zgarganda formani qayta to'ldiradi
	// (`UserFormModal` dagi bilan bir xil sabab). `toInitialValues` har renderda
	// yangi obyekt qaytarsa, `isSubmitting` almashuvi ham yetardi: backend
	// jadvalni rad etganda (masalan `crossesMidnightMismatch`) foydalanuvchining
	// barcha tahrirlari serverdagi qiymatlarga qaytarilardi.
	const initialValues = useMemo(
		() => (editing ? toInitialValues(editing) : NEW_SCHEDULE_VALUES),
		[editing]
	);

	return (
		<FormModal<ScheduleFormValues>
			open={open}
			width={MODAL_WIDTH}
			title={
				isEditing
					? t("scheduleForm.editTitle", { name: editing.name })
					: t("scheduleForm.createTitle")
			}
			initialValues={initialValues}
			isSubmitting={isSubmitting}
			onCancel={onCancel}
			onSubmit={(values) => onSubmit(toScheduleInput(values))}
		>
			<Form.Item
				name="name"
				label={t("scheduleForm.name.label")}
				rules={[{ required: true, message: t("scheduleForm.name.required") }]}
			>
				<Input placeholder={t("scheduleForm.name.placeholder")} />
			</Form.Item>

			<Form.Item
				name="shiftType"
				label={t("scheduleForm.shiftTypeLabel")}
				rules={[{ required: true }]}
			>
				<Select options={shiftTypeOptions} />
			</Form.Item>

			<Form.Item
				name="patternKind"
				label={t("scheduleForm.patternKindLabel")}
				rules={[{ required: true }]}
			>
				<Select options={patternKindOptions} />
			</Form.Item>

			{/*
			 * Shoxlanish `shouldUpdate` render-prop orqali: `FormModal` forma
			 * instansiyasini tashqariga bermaydi, ya'ni `Form.useWatch` bu
			 * yerda ishlatib bo'lmaydi. Render-prop antd'ning o'z yechimi va
			 * qo'shimcha prop talab qilmaydi.
			 */}
			<Form.Item noStyle shouldUpdate={(prev, cur) => prev.patternKind !== cur.patternKind}>
				{({ getFieldValue }) =>
					getFieldValue("patternKind") === "weekly" ? <WeeklyFields /> : <CyclicFields />
				}
			</Form.Item>

			<Form.Item
				name="startTime"
				label={t("scheduleForm.startTime.label")}
				rules={[{ required: true, message: t("scheduleForm.startTime.required") }]}
			>
				<TimePicker format={TIME_FORMAT} className="w-full" />
			</Form.Item>

			<Form.Item
				name="endTime"
				label={t("scheduleForm.endTime.label")}
				rules={[{ required: true, message: t("scheduleForm.endTime.required") }]}
			>
				<TimePicker format={TIME_FORMAT} className="w-full" />
			</Form.Item>

			<Form.Item name="lunchStart" label={t("scheduleForm.lunchStart")}>
				<TimePicker format={TIME_FORMAT} className="w-full" />
			</Form.Item>

			<Form.Item name="lunchEnd" label={t("scheduleForm.lunchEnd")}>
				<TimePicker format={TIME_FORMAT} className="w-full" />
			</Form.Item>

			<Form.Item
				name="crossesMidnight"
				label={t("scheduleForm.crossesMidnight")}
				valuePropName="checked"
			>
				<Switch />
			</Form.Item>

			<Alert
				type="info"
				showIcon
				title={t("scheduleForm.nightShiftInfo.title")}
				description={t("scheduleForm.nightShiftInfo.description")}
			/>
		</FormModal>
	);
}
